import { Link } from 'react-router-dom';
import { ArrowUpRight, Zap } from 'lucide-react';
import { useCatalog } from '../hooks/useCatalog';

const API = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith('/api/') ? `${API}${u}` : u);

export default function CategoryGrid() {
  const { categoryTree, products } = useCatalog();
  const productCount = (slug) =>
    products.filter((p) => p.category === slug).length;

  // Featured = first category (UPS); all remaining categories in the 2-col grid
  const featured = categoryTree[0];
  const rest = categoryTree.slice(1);

  if (!featured) return null;

  return (
    <section className="section-pad bg-white" data-testid="home-category-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div className="max-w-2xl">
            <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
              KATEGORI PRODUK
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-neutral-900 leading-[1.05]">
              Power untuk setiap kebutuhan.
            </h2>
          </div>
          <Link
            to="/products"
            className="inline-flex items-center gap-1 text-sm font-semibold text-neutral-900 border-b-2 border-px-red pb-1 hover:gap-2 transition-all"
          >
            Lihat semua produk <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        {/* 12-col asymmetric bento */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5">
          {/* FEATURED (7 cols) */}
          <Link
            to={`/products?category=${featured.slug}`}
            data-testid={`home-category-${featured.slug}`}
            className="group relative lg:col-span-7 h-[420px] lg:h-[560px] rounded-3xl overflow-hidden bg-neutral-900"
          >
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-[900ms] group-hover:scale-105"
              style={{ backgroundImage: `url(${absUrl(featured.image)})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" />

            <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] text-white font-semibold bg-px-red px-3 py-1.5 rounded-full">
              <Zap className="w-3 h-3" /> FLAGSHIP
            </span>

            <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10 text-white">
              <p className="text-[11px] tracking-[0.3em] text-red-300 font-semibold mb-3">
                {featured.subtitle.toUpperCase()}
              </p>
              <div className="flex items-baseline gap-3 mb-4 flex-wrap">
                <h3 className="font-display text-4xl lg:text-6xl font-bold leading-none">
                  {featured.title}
                </h3>
                <span className="text-sm text-neutral-300">
                  · {productCount(featured.slug)} produk
                </span>
              </div>
              <p className="text-neutral-200 leading-relaxed mb-6 max-w-lg">
                {featured.description}
              </p>
              {featured.subcategories?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-6">
                  {featured.subcategories.slice(0, 5).map((s) => (
                    <span
                      key={s.slug}
                      className="text-[10px] tracking-wider px-2.5 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20"
                    >
                      {s.title}
                    </span>
                  ))}
                </div>
              )}
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold border-b-2 border-px-red pb-0.5 w-fit group-hover:gap-2.5 transition-all">
                Jelajahi <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          {/* SMALLER CARDS (5 cols · 2-col grid; odd last spans full width) */}
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
            {rest.map((c, i) => (
              <Link
                key={c.slug}
                to={`/products?category=${c.slug}`}
                data-testid={`home-category-${c.slug}`}
                className={`group relative h-[220px] lg:h-[270px] rounded-2xl overflow-hidden bg-neutral-100 ${
                  rest.length % 2 === 1 && i === rest.length - 1
                    ? 'sm:col-span-2'
                    : ''
                }`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                  style={{ backgroundImage: `url(${absUrl(c.image)})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/5" />
                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <p className="text-[10px] tracking-[0.25em] text-red-300 font-semibold mb-1">
                    {c.subtitle.toUpperCase()}
                  </p>
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <h4 className="font-display text-xl lg:text-2xl font-bold leading-tight">
                      {c.title}
                    </h4>
                    <span className="text-[11px] text-neutral-300 flex-shrink-0">
                      {productCount(c.slug)} produk
                    </span>
                  </div>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold border-b border-px-red pb-0.5 w-fit opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                    Jelajahi <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
